import { Badge, Box, Flex, Table, Text } from '@radix-ui/themes'
import cn from 'classnames'
import { CopyButton } from '~/components/CopyButton'
import { SectionCard } from '~/components/SectionCard'
import type { StationDetail, StationSectionProps } from '../types'

type StationDataSource = StationDetail['data_sources'][number]

const UNIT_LABELS: Record<StationDataSource['unit'], string> = { m3: 'm³', l: 'L' }

const READING_METHOD_LABELS: Record<StationDataSource['reading_method'], string> = {
  cumulative: 'Cumulative',
  incremental: 'Incremental'
}

const isRetired = (source: StationDataSource): source is StationDataSource & { retired_at: string } =>
  source.retired_at !== null

const activeFirst = (sources: StationDataSource[]): StationDataSource[] =>
  [...sources].sort((a, b) => Number(isRetired(a)) - Number(isRetired(b)))

const NoValue = () => (
  <Text size="1" color="gray">
    —
  </Text>
)

interface DataSourceRowProps {
  source: StationDataSource
}

const DataSourceRow = ({ source }: DataSourceRowProps) => (
  <Table.Row className={cn(isRetired(source) && 'text-[var(--gray-11)]')}>
    <Table.Cell>
      <Flex direction="column" align="start" gap="1">
        <Text size="2">{source.source_type}</Text>
        {isRetired(source) ? (
          <Badge color="amber" variant="soft" title={source.retired_at}>
            Retired · {source.retired_at.slice(0, 10)}
          </Badge>
        ) : null}
      </Flex>
    </Table.Cell>

    <Table.Cell>{source.maker ? <Text size="2">{source.maker}</Text> : <NoValue />}</Table.Cell>

    <Table.Cell>
      {source.provider_device_id ? (
        <Flex direction="column" align="start" gap="1">
          <Flex align="center" gap="2">
            <Text size="2" className="font-mono">
              {source.provider_device_id}
            </Text>
            <CopyButton value={source.provider_device_id} label={`Copy device ID ${source.provider_device_id}`} />
          </Flex>
          {source.measurement_point_id !== null ? (
            <Text size="1" color="gray">
              Measurement point {source.measurement_point_id}
            </Text>
          ) : null}
        </Flex>
      ) : (
        <NoValue />
      )}
    </Table.Cell>

    <Table.Cell>
      <Text size="2">{UNIT_LABELS[source.unit]}</Text>
    </Table.Cell>

    <Table.Cell>
      <Text size="2">{READING_METHOD_LABELS[source.reading_method]}</Text>
    </Table.Cell>

    <Table.Cell>
      {source.notes ? (
        <Box maxWidth="320px">
          <Text size="2" className="whitespace-pre-wrap break-words">
            {source.notes}
          </Text>
        </Box>
      ) : (
        <NoValue />
      )}
    </Table.Cell>
  </Table.Row>
)

export const StationDataSourcesCard = ({ station }: StationSectionProps) => (
  <SectionCard title="Data sources">
    {station.data_sources.length === 0 ? (
      <Text size="2" color="gray">
        No data sources attached — nothing is reporting data into this station.
      </Text>
    ) : (
      <Table.Root variant="surface">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>Source type</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Maker</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Provider device ID</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Unit</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Reading method</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Notes</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {activeFirst(station.data_sources).map((source) => (
            <DataSourceRow key={source.id} source={source} />
          ))}
        </Table.Body>
      </Table.Root>
    )}
  </SectionCard>
)
